import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Pencil, X, Check, Trash2, Image, Video, File, Bold, Italic,
  Strikethrough, Upload, Loader, MessageSquare, Variable, AlertTriangle,
} from "lucide-react";
import { uploadDocumentFile } from "../../apis/documents";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import { getUnknownVariableNames, getVariableNamesFromText } from "../../utils/flowVariableValidation";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const MAX_BUTTONS = 3;
const MAX_LABEL_LEN = 20;
const MAX_PAYLOAD_LEN = 256;
const MAX_BODY_LEN = 1024;
const MAX_HEADER_LEN = 60;
const MAX_FOOTER_LEN = 60;

/* ── Collapsed WhatsApp-style preview ─────────────────────────── */
const CollapsedView = ({ data, onEdit }) => {
  const bodyText = data.body?.text || "";
  const footerText = data.footer?.text || "";
  const headerFormat = data.header?.format || "NONE";
  const buttons = data.buttons || [];

  let displayBody = bodyText
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    .replace(/~(.*?)~/g, "<del>$1</del>");

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: "#EFEAE2", padding: "10px 10px 6px", minWidth: "260px", maxWidth: "300px" }}>
      <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg" title="Edit">
        <Pencil size={12} className="text-teal-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm relative">
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden">
          <div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" />
        </div>
        <div className="px-2 py-1.5">
          {/* Header */}
          {headerFormat === "TEXT" && data.header?.text && (
            <div className="mb-1 -mx-2 -mt-1.5 px-2 py-1.5 bg-[#F0F0F0] rounded-t-lg">
              <p className="text-[12px] font-semibold text-[#111B21] leading-tight">{data.header.text}</p>
            </div>
          )}
          {headerFormat === "IMAGE" && (
            <div className="-mx-2 -mt-1.5 rounded-t-lg overflow-hidden bg-[#F0F0F0] flex items-center justify-center mb-1" style={{ maxHeight: 100 }}>
              {data.header?.mediaUrl && !data.header.mediaUrl.includes("{{") ? (
                <img src={data.header.mediaUrl} alt="" className="w-full object-cover" style={{ maxHeight: 100 }} />
              ) : (
                <div className="py-4 text-center">
                  <Image size={20} className="text-gray-300 mx-auto" />
                  <p className="text-[9px] text-gray-400 mt-1">{data.header?.mediaUrl?.includes("{{") ? `From ${getVariableNamesFromText(data.header.mediaUrl).map(v => `{{${v}}}`).join(", ")}` : "Image"}</p>
                </div>
              )}
            </div>
          )}
          {headerFormat === "VIDEO" && (
            <div className="-mx-2 -mt-1.5 rounded-t-lg overflow-hidden bg-black flex items-center justify-center mb-1" style={{ maxHeight: 100 }}>
              <div className="py-4 text-center"><Video size={20} className="text-white/30 mx-auto" /><p className="text-[9px] text-white/30 mt-1">Video</p></div>
            </div>
          )}
          {headerFormat === "DOCUMENT" && (
            <div className="-mx-2 -mt-1.5 px-2 py-2 bg-[#F0F0F0] rounded-t-lg flex items-center gap-2 mb-1">
              <File size={14} className="text-red-500" />
              <span className="text-[11px] font-medium truncate flex-1">{data.header?.fileName || "Document"}</span>
            </div>
          )}

          {/* Body */}
          {displayBody ? (
            <div className="text-[12px] text-[#111B21] whitespace-pre-wrap break-words leading-[17px]" dangerouslySetInnerHTML={{ __html: displayBody.length > 120 ? displayBody.substring(0, 120) + "..." : displayBody }} />
          ) : (
            <p className="text-[12px] text-gray-400 italic">Type a message...</p>
          )}

          {/* Footer */}
          {footerText && <p className="text-[10px] text-[#667781] mt-1 leading-tight">{footerText}</p>}

          {/* Timestamp */}
          <div className="flex items-center justify-end mt-0.5 -mb-0.5">
            <span className="text-[9px] text-[#667781]">{new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}</span>
          </div>
        </div>

        {/* Buttons */}
        {buttons.length > 0 && (
          <div className="border-t border-gray-100">
            {buttons.map((btn, i) => (
              <div key={btn.id} className={`flex flex-col items-center justify-center py-[7px] ${i < buttons.length - 1 ? "border-b border-gray-100" : ""}`}>
                <div className="flex items-center gap-1.5">
                  <MessageSquare size={12} className="text-[#027EB5]" />
                  <span className="text-[12px] font-medium text-[#027EB5]">{btn.label || `Button ${i + 1}`}</span>
                </div>
                {btn.payload && btn.payload !== btn.label && (
                  <span className="text-[8px] text-[#8696a0] mt-0.5">payload: {btn.payload}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Handle labels */}
      {buttons.length > 0 && (
        <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
          {buttons.map((btn) => (
            <span key={btn.id} className="text-[7px] text-[#667781] font-semibold truncate max-w-[60px] text-center" title={btn.label || "Button"}>
              {btn.label || "Button"}
            </span>
          ))}
          <span className="text-[7px] text-[#F59E0B] font-semibold truncate max-w-[60px] text-center" title="Other">Other</span>
        </div>
      )}
    </div>
  );
};

/* ── Expanded Editor ──────────────────────────────────────────── */
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => ({
    header: { format: data.header?.format || "NONE", text: data.header?.text || "", mediaUrl: data.header?.mediaUrl || "", fileName: data.header?.fileName || "" },
    body: { text: data.body?.text || "", variables: data.body?.variables ? data.body.variables.map(v => ({ ...v })) : [] },
    footer: { text: data.footer?.text || "" },
    buttons: data.buttons ? data.buttons.map(b => ({ ...b })) : [],
    selectionVariable: data.selectionVariable || "",
  }));
  const [uploading, setUploading] = useState(false);
  const [showVarPicker, setShowVarPicker] = useState(false);
  const bodyRef = useRef(null);
  const mediaUrlRef = useRef(null);
  const fileInputRef = useRef(null);

  const updateField = (path, value) => {
    setForm(prev => {
      const parts = path.split(".");
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) { obj[parts[i]] = { ...obj[parts[i]] }; obj = obj[parts[i]]; }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await uploadDocumentFile(file);
      updateField("header.mediaUrl", response.data.data.doc_url);
      updateField("header.fileName", file.name);
    } catch { /* ignore */ } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const insertVariableInMediaUrl = (varName) => {
    if (!varName) return;
    const cur = form.header.mediaUrl || "";
    const input = mediaUrlRef.current;
    const pos = input?.selectionStart ?? cur.length;
    updateField("header.mediaUrl", cur.slice(0, pos) + `{{${varName}}}` + cur.slice(pos));
  };

  const insertVariable = (varName) => {
    if (!varName) return;
    if (!form.body.variables.some(v => v.name === varName)) {
      updateField("body.variables", [...form.body.variables, { name: varName, example: "" }]);
    }
    const textarea = bodyRef.current;
    const placeholder = `{{${varName}}}`;
    const pos = textarea?.selectionStart || form.body.text.length;
    updateField("body.text", form.body.text.slice(0, pos) + placeholder + form.body.text.slice(pos));
  };

  const applyFormat = (fmt) => {
    const ta = bodyRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const m = { bold: "*", italic: "_", strikethrough: "~" }[fmt] || "*";
    const sel = form.body.text.substring(s, e) || "text";
    updateField("body.text", form.body.text.substring(0, s) + `${m}${sel}${m}` + form.body.text.substring(e));
  };

  const addButton = () => {
    if (form.buttons.length >= MAX_BUTTONS) return;
    updateField("buttons", [...form.buttons, { id: `btn_${Date.now()}`, label: "", payload: "" }]);
  };

  const updateButton = (id, field, value) => {
    updateField("buttons", form.buttons.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const removeButton = (id) => {
    updateField("buttons", form.buttons.filter(b => b.id !== id));
  };

  const getAcceptType = () => {
    switch (form.header.format) {
      case "IMAGE": return "image/png,image/jpeg,image/jpg,image/webp";
      case "VIDEO": return "video/mp4,video/avi,video/webm";
      case "DOCUMENT": return ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
      default: return "*";
    }
  };

  return (
    <div className="nowheel" style={{ backgroundColor: "#EFEAE2", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "420px" }} onClick={e => e.stopPropagation()}>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-teal-500" />
          <span className="text-[13px] font-bold text-[#111B21]">Interactive Buttons</span>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => onSave(form)} className="px-3 py-1.5 text-[11px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1 shadow-sm"><Check size={12} />Save</button>
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
        </div>
      </div>

      {/* Message bubble editing area */}
      <NodeEditorTabs nodeType="interactiveButtons">      <div className="bg-white rounded-lg shadow-sm mb-3 relative">
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden"><div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" /></div>

        {/* Header */}
        <div className="px-3 pt-2.5 pb-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Header</span>
            <select value={form.header.format} onChange={e => { updateField("header.format", e.target.value); if (["NONE","TEXT"].includes(e.target.value)) { updateField("header.mediaUrl",""); updateField("header.fileName",""); } }} className="text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border-0 focus:outline-none cursor-pointer">
              <option value="NONE">None</option>
              <option value="TEXT">Text</option>
              <option value="IMAGE">Image</option>
              <option value="VIDEO">Video</option>
              <option value="DOCUMENT">Document</option>
            </select>
          </div>
          {form.header.format === "TEXT" && (
            <input value={form.header.text} onChange={e => updateField("header.text", e.target.value)} placeholder="Header text..." maxLength={MAX_HEADER_LEN} className="w-full px-0 py-1 text-[13px] font-semibold text-[#111B21] border-0 border-b border-gray-200 focus:border-teal-500 focus:outline-none bg-transparent placeholder:text-gray-400" />
          )}
          {["IMAGE","VIDEO","DOCUMENT"].includes(form.header.format) && (
            <div className="mb-1 space-y-1.5">
              <div className="flex items-center gap-1 mb-1">
                <input ref={mediaUrlRef} type="text" value={form.header.mediaUrl} onChange={e => updateField("header.mediaUrl", e.target.value)} placeholder="https://... or {{variable}}" className="flex-1 px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-teal-500 placeholder:text-gray-400 font-medium" />
                <VariableDropdown value="" onChange={name => { if(name) insertVariableInMediaUrl(name); }} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Var" className="w-20" />
              </div>
              <label className="block cursor-pointer">
                <div className={`rounded-lg p-2 text-center transition-all ${uploading ? "bg-teal-50 border border-teal-300" : "bg-[#F0F0F0] hover:bg-[#E8E8E8] border border-transparent"}`}>
                  {uploading ? <div className="flex items-center justify-center gap-2"><Loader size={14} className="text-teal-600 animate-spin" /><span className="text-[10px] text-teal-600 font-semibold">Uploading...</span></div> : <div className="flex items-center justify-center gap-1.5"><Upload size={14} className="text-[#667781]" /><span className="text-[10px] font-medium text-[#667781]">Upload {form.header.format.toLowerCase()}</span></div>}
                </div>
                <input ref={fileInputRef} type="file" className="hidden" accept={getAcceptType()} onChange={handleFileUpload} disabled={uploading} />
              </label>
            </div>
          )}
        </div>

        <div className="mx-3 border-t border-gray-100" />

        {/* Body */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Body</span>
            <div className="flex items-center gap-0.5">
              <button onClick={() => applyFormat("bold")} className="p-1 hover:bg-[#F0F0F0] rounded"><Bold size={11} className="text-[#667781]" /></button>
              <button onClick={() => applyFormat("italic")} className="p-1 hover:bg-[#F0F0F0] rounded"><Italic size={11} className="text-[#667781]" /></button>
              <button onClick={() => applyFormat("strikethrough")} className="p-1 hover:bg-[#F0F0F0] rounded"><Strikethrough size={11} className="text-[#667781]" /></button>
              <div className="w-px h-3 bg-gray-200 mx-0.5" />
              <div className="relative">
                <button onClick={() => setShowVarPicker(!showVarPicker)} className="text-[9px] px-1.5 py-0.5 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 flex items-center gap-0.5"><Variable size={8} />Var</button>
                {showVarPicker && (
                  <div className="absolute right-0 top-full mt-1 z-50 w-48">
                    <VariableDropdown value="" onChange={name => { if(name) insertVariable(name); setShowVarPicker(false); }} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Pick variable..." />
                  </div>
                )}
              </div>
            </div>
          </div>
          <textarea ref={bodyRef} value={form.body.text} onChange={e => updateField("body.text", e.target.value)} placeholder="Type a message..." rows={3} maxLength={MAX_BODY_LEN} className="w-full px-0 py-1 text-[12px] text-[#111B21] border-0 focus:outline-none resize-none bg-transparent placeholder:text-[#8696a0] leading-[17px]" />
          <div className="flex justify-between">
            <span className="text-[9px] text-[#8696a0]">*bold* _italic_ ~strike~</span>
            <span className="text-[9px] text-[#8696a0]">{form.body.text.length}/{MAX_BODY_LEN}</span>
          </div>
          {(() => {
            const unknowns = getUnknownVariableNames(form.body.text, flowVariables);
            return unknowns.length > 0 ? (
              <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
                <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
                <span>Unknown: <strong>{unknowns.join(", ")}</strong></span>
              </div>
            ) : null;
          })()}
        </div>

        <div className="mx-3 border-t border-gray-100" />

        {/* Footer */}
        <div className="px-3 py-2">
          <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Footer</span>
          <input type="text" value={form.footer.text} onChange={e => updateField("footer.text", e.target.value)} placeholder="Optional footer..." maxLength={MAX_FOOTER_LEN} className="w-full px-0 py-1 text-[11px] text-[#667781] border-0 focus:outline-none bg-transparent placeholder:text-[#8696a0]" />
        </div>
      </div>

      {/* Buttons section */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Reply Buttons ({form.buttons.length}/{MAX_BUTTONS})</span>
          <button onClick={addButton} disabled={form.buttons.length >= MAX_BUTTONS} className="text-[10px] px-2.5 py-1 bg-white text-teal-600 border border-teal-200 rounded-full font-semibold hover:bg-teal-50 disabled:opacity-30 flex items-center gap-1 transition-colors">
            <MessageSquare size={9} /> Add
          </button>
        </div>
        {form.buttons.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {form.buttons.map((btn, i) => (
              <div key={btn.id} className={`p-2.5 ${i < form.buttons.length - 1 ? "border-b border-gray-100" : ""}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold text-teal-600">Button {i + 1}</span>
                  <button onClick={() => removeButton(btn.id)} className="p-0.5 text-[#667781] hover:text-red-500 rounded transition-colors"><Trash2 size={11} /></button>
                </div>
                <input type="text" value={btn.label || ""} onChange={e => updateButton(btn.id, "label", e.target.value)} placeholder="Button label (display text)" maxLength={MAX_LABEL_LEN} className="w-full px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30 placeholder:text-[#8696a0] mb-1" />
                <input type="text" value={btn.payload || ""} onChange={e => updateButton(btn.id, "payload", e.target.value)} placeholder="Payload (internal value for routing)" maxLength={MAX_PAYLOAD_LEN} className="w-full px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30 placeholder:text-[#8696a0] font-mono" />
                {btn.payload && btn.payload !== btn.label && (
                  <p className="text-[8px] text-teal-600 mt-0.5 px-1">Stored value: {btn.payload}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Store selection */}
      <div className="mb-3">
        <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">Store selection in variable</label>
        <VariableDropdown value={form.selectionVariable || ""} onChange={name => updateField("selectionVariable", name)} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Optional: save selection..." />
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2">
        <button onClick={() => onSave(form)} className="flex-1 py-2 text-[12px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"><Check size={14} />Save</button>
        <button onClick={onCancel} className="flex-1 py-2 text-[12px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
      </div>
    </div>
  );
};

/* ── Main Node ────────────────────────────────────────────────── */
const InteractiveButtonsNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("interactiveButtons");

  const handleSave = useCallback((formData) => {
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, { ...data, ...formData });
    }
    closeEditor();
  }, [id, data, closeEditor]);

  const buttons = data.buttons || [];

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle type="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-teal-500 !border-2 !border-white !shadow-sm" style={{ left: -7 }} />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor data={data} onSave={handleSave} presentation="fullscreen" onCancel={closeEditor} flowVariables={data.flowVariables || []} onCreateVariable={data.onCreateVariable} />
      </FlowNodeEditorShell>

      {/* Source handles: one per button + "Other" */}
      {buttons.length > 0 ? (
        <>
          {buttons.map((btn, index) => {
            const pct = ((index + 1) / (buttons.length + 2)) * 100;
            return (
              <Handle key={btn.id} type="source" position={Position.Right} id={btn.id} style={{ top: `${pct}%`, right: -7 }} className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-teal-500" title={btn.label || "Button"} />
            );
          })}
          <Handle key="other" type="source" position={Position.Right} id="other" style={{ top: `${((buttons.length + 1) / (buttons.length + 2)) * 100}%`, right: -7 }} className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-[#F59E0B]" title="Other" />
        </>
      ) : (
        <Handle type="source" position={Position.Right} id="default" className="!w-3.5 !h-3.5 !bg-teal-500 !border-2 !border-white !shadow-sm" style={{ right: -7 }} />
      )}
    </div>
  );
};

export default InteractiveButtonsNode;
